/* Lightweight permission probe for the admin FE.
 *
 * The backend's permissions matrix (Task 13 + Task 14) is the source of truth
 * — every gated endpoint already uses ``require_permission(resource, action)``
 * server-side. This hook lets the FE *anticipate* a 403 (e.g. to render a
 * disabled button with a "no permission" tooltip) instead of waiting for the
 * click → 403 → silent fail UX.
 *
 * Strategy:
 *  - On first use, fetch ``GET /admin/permissions/matrix`` once per session
 *    (the endpoint is accessible to admin/manager/super_admin per the catalog).
 *  - Cache the resolved (resource, action) → roles[] map in module scope.
 *  - Return { ready, hasPermission(resource, action) }.
 *  - ``hasPermission`` returns ``false`` while loading and on fetch errors —
 *    callers should also defensively disable the UI until ``ready === true``.
 *  - Re-fetch on every ``user.role`` change (covers impersonation start/stop).
 *
 * No prop drilling, no provider — the hook reads the role straight from
 * ``useAuth()``.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

let _cache = { role: null, entries: null, loading: null };

async function _loadMatrix(role) {
    if (_cache.role === role && _cache.entries !== null) return _cache.entries;
    if (_cache.role === role && _cache.loading) return _cache.loading;
    const p = (async () => {
        try {
            const { data } = await api.get("/admin/permissions/matrix");
            const map = new Map();
            (data?.permissions || []).forEach((p) => {
                map.set(`${p.resource}::${p.action}`, p.roles || []);
            });
            _cache = { role, entries: map, loading: null };
            return map;
        } catch (_) {
            _cache = { role, entries: new Map(), loading: null };
            return _cache.entries;
        }
    })();
    _cache = { role, entries: null, loading: p };
    return p;
}

export function usePermission() {
    const { user } = useAuth();
    const role = user?.role || null;
    const [ready, setReady] = useState(_cache.role === role && _cache.entries !== null);

    useEffect(() => {
        let cancelled = false;
        if (!role) {
            setReady(false);
            return () => { cancelled = true; };
        }
        if (_cache.role !== role) _cache = { role: null, entries: null, loading: null };
        _loadMatrix(role).then(() => { if (!cancelled) setReady(true); });
        return () => { cancelled = true; };
    }, [role]);

    const hasPermission = (resource, action) => {
        if (!role) return false;
        if (role === "super_admin") return true;  // super_admin bypass — defense in depth
        const entries = _cache.entries;
        if (!entries) return false;
        const roles = entries.get(`${resource}::${action}`) || [];
        return roles.includes(role);
    };

    return { ready, hasPermission };
}
